import { createStore, createHook, createContainer, type StoreActionApi } from 'react-sweet-state';
import { ff } from '@atlassian/jira-feature-flagging';
import { DEFAULT_ITEM_HEIGHT } from '../../constants';
import {
	getHierarchyEnrichedItems,
	getHierarchyEnrichedItemsOld,
	getItemsContainerStyle,
	getSelectedItemIdsHash,
} from '../../utils';
import { actions, type Actions } from './actions';
import {
	getIsTableEmpty,
	getItems,
	getIsItemSelected,
	getItemsContainerStyles,
	getItemsCount,
	getAriaRowCount,
	getCreateItemAnchor,
	getHasActiveCreate,
	getItemHeight,
} from './selectors';
import type { ContainerProps, State } from './types';

const DEFAULT_STATE: State = {
	items: [],
	itemsSelectionState: {},
	ariaRowCount: 0,
	isTableEmpty: false,
	itemsBackground: 'none',
	totalHeight: 0,
	itemHeight: DEFAULT_ITEM_HEIGHT,
	createItemAnchor: undefined,
};

const store = createStore<State, Actions>({
	initialState: DEFAULT_STATE,
	actions,
	name: 'timeline-table.items',
});

export const useItemsState = createHook(store);

export const useItems = createHook(store, {
	selector: getItems,
});

export const useIsItemSelected = createHook(store, {
	selector: getIsItemSelected,
});

export const useIsTableEmpty = createHook(store, {
	selector: getIsTableEmpty,
});

export const useItemsCount = createHook(store, {
	selector: getItemsCount,
});

export const useAriaRowCount = createHook(store, {
	selector: getAriaRowCount,
});

export const useItemHeight = createHook(store, {
	selector: getItemHeight,
});

export const useItemsContainerStyles = createHook(store, {
	selector: getItemsContainerStyles,
});

export const useCreateItemAnchor = createHook(store, {
	selector: getCreateItemAnchor,
});

export const useHasActiveCreate = createHook(store, {
	selector: getHasActiveCreate,
});

const updateState =
	() =>
	(
		{ setState }: StoreActionApi<State>,
		{
			items,
			createItemAnchor,
			expandedItems,
			selectedItemIdsWithPermission,
			isCreateLastEnabled,
			isCreateSiblingEnabled,
			isCreateChildEnabled,
			itemHeight,
			ariaRowOffset,
		}: ContainerProps,
	) => {
		const { flatItems, ariaRowCount } = ff('jwm.visual-hierarchy-milestone-1')
			? getHierarchyEnrichedItems(
					items,
					createItemAnchor,
					expandedItems,
					isCreateLastEnabled,
					isCreateSiblingEnabled,
					isCreateChildEnabled,
					itemHeight,
					ariaRowOffset,
				)
			: getHierarchyEnrichedItemsOld(
					items,
					createItemAnchor,
					expandedItems,
					isCreateLastEnabled,
					isCreateSiblingEnabled,
					isCreateChildEnabled,
					itemHeight,
					ariaRowOffset,
				);

		const itemsSelectionState = getSelectedItemIdsHash(selectedItemIdsWithPermission);
		const { itemsBackground, totalHeight } = getItemsContainerStyle(flatItems, itemHeight);

		setState({
			items: flatItems,
			itemsSelectionState,
			ariaRowCount,
			isTableEmpty: items.length === 0,
			itemsBackground,
			totalHeight,
			itemHeight,
			createItemAnchor,
		});
	};

export const ItemsProvider = createContainer<State, Actions, ContainerProps>(store, {
	onInit: updateState,
	onUpdate: updateState,
});
